import React, { useState, useEffect } from "react";
import { Tooltip } from "react-tooltip";
import { Link } from "react-router-dom";
import axios from "axios";
import { getLanguageText, truncateLanguageText } from "../../utils/multilanguageUtils";
import "./LocationManagerCard.css";

const LocationManagerCard = ({
  userId,
  buttonColor,
  showMsgSuccessToast,
  showErrorToast,
  currentLanguage = 'en', // Add currentLanguage prop
}) => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubcategories, setExpandedSubcategories] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [availableLanguages, setAvailableLanguages] = useState(['en']);
  const [locationOrders, setLocationOrders] = useState(new Map()); // Map of locationId -> order

  // New location form states
  const [showAddLocationForm, setShowAddLocationForm] = useState(false);
  const [newLocationName, setNewLocationName] = useState("");
  const [newLocationCategory, setNewLocationCategory] = useState("");
  const [newLocationSubcategory, setNewLocationSubcategory] = useState("");
  const [isAddingLocation, setIsAddingLocation] = useState(false);

  // Quick add location to specific parent (category or subcategory)
  const [quickAddForms, setQuickAddForms] = useState({}); // { parentId: { show: true, name: '', loading: false } }

  useEffect(() => {
    if (userId) {
      fetchData();
      fetchAvailableLanguages();
      fetchLocationOrders();
    }
  }, [userId]);

  const fetchAvailableLanguages = async () => {
    try {
      // Fetch enabled languages from the language settings API
      const response = await axios.get(`/api/language/enabled/${userId}`);
      if (response.data.success && response.data.languages) {
        const enabledLangCodes = response.data.languages;
        setAvailableLanguages(enabledLangCodes.length > 0 ? enabledLangCodes : ['en']);
        console.log('Available enabled languages:', enabledLangCodes);
      } else {
        setAvailableLanguages(['en']);
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      setAvailableLanguages(['en']);
    }
  };

  const fetchLocationOrders = async () => {
    try {
      // Fetch location orders for all categories and subcategories
      const response = await axios.get(`/api/locationOrder/location-orders-by-parent/${userId}`);
      
      if (response.data.success && response.data.data) {
        const orderMap = new Map();
        
        // Process orders grouped by parent (category/subcategory)
        response.data.data.forEach(parentOrder => {
          const parentKey = `${parentOrder.parentType}_${parentOrder.parentId}`;
          
          if (parentOrder.locationData && Array.isArray(parentOrder.locationData)) {
            parentOrder.locationData.forEach(item => {
              if (item.locationId && item.order !== undefined) {
                // Store with composite key: parentType_parentId_locationId
                const key = `${parentKey}_${item.locationId}`;
                orderMap.set(key, item.order);
              }
            });
          }
        });
        
        setLocationOrders(orderMap);
        console.log('Location orders loaded:', orderMap.size, 'location-parent combinations');
      } else {
        setLocationOrders(new Map());
      }
    } catch (error) {
      console.error('Error fetching location orders:', error);
      // Don't show error toast - it's okay if there are no orders yet
      setLocationOrders(new Map());
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [categoriesRes, subcategoriesRes, locationsRes] = await Promise.all(
        [
          axios.get(`/api/wayfinder/get-categories/${userId}`),
          axios.get(`/api/wayfinder/subcategory/${userId}`),
          axios.get(`/api/wayfinder/get-locations/${userId}`),
        ],
      );

      setCategories(categoriesRes.data.data || []);
      setSubcategories(subcategoriesRes.data.data || []);
      setLocations(locationsRes.data.data || []);
      
      // Also refresh location orders
      await fetchLocationOrders();
    } catch (error) {
      console.error("Error fetching data:", error);
      showErrorToast("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const toggleSubcategory = (subcategoryId) => {
    setExpandedSubcategories((prev) => ({
      ...prev,
      [subcategoryId]: !prev[subcategoryId],
    }));
  };

  const toggleCategoryVisibility = async (categoryId, currentStatus) => {
    try {
      await axios.patch(
        `/api/wayfinder/wayfinder-category-visibility/${userId}/${categoryId}/status`,
        {
          status: !currentStatus,
        },
      );
      await fetchData();
      showMsgSuccessToast(
        `Category ${!currentStatus ? "shown" : "hidden"} successfully!`,
      );
    } catch (error) {
      console.error("Error toggling category:", error);
      showErrorToast("Failed to update category visibility");
    }
  };

  const toggleSubcategoryVisibility = async (subcategoryId, currentStatus) => {
    try {
      await axios.patch(`/api/wayfinder/subcategory/${subcategoryId}/status`, {
        status: !currentStatus,
      });
      await fetchData();
      showMsgSuccessToast(
        `Subcategory ${!currentStatus ? "shown" : "hidden"} successfully!`,
      );
    } catch (error) {
      console.error("Error toggling subcategory:", error);
      showErrorToast("Failed to update subcategory visibility");
    }
  };

  const toggleLocationVisibility = async (locationId, currentStatus) => {
    try {
      await axios.patch(`/api/wayfinder/locations/${locationId}/status`, {
        status: !currentStatus,
        userId: userId,
      });
      await fetchData();
      showMsgSuccessToast(
        `Location ${!currentStatus ? "shown" : "hidden"} successfully!`,
      );
    } catch (error) {
      console.error("Error toggling location:", error);
      showErrorToast("Failed to update location visibility");
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?"))
      return;
    try {
      await axios.delete(
        `/api/wayfinder/delete-wayfinder-category/${categoryId}`,
        {
          data: { userId },
        },
      );
      await fetchData();
      showMsgSuccessToast("Category deleted successfully!");
    } catch (error) {
      console.error("Error deleting category:", error);
      showErrorToast("Failed to delete category");
    }
  };

  const deleteSubcategory = async (subcategoryId) => {
    if (!window.confirm("Are you sure you want to delete this subcategory?"))
      return;
    try {
      await axios.delete(
        `/api/wayfinder/delete-wayfinder-subCategory/${subcategoryId}`,
        {
          data: { userId },
        },
      );
      await fetchData();
      showMsgSuccessToast("Subcategory deleted successfully!");
    } catch (error) {
      console.error("Error deleting subcategory:", error);
      showErrorToast("Failed to delete subcategory");
    }
  };

  const deleteLocation = async (locationId) => {
    if (!window.confirm("Are you sure you want to delete this location?"))
      return;
    try {
      await axios.delete(
        `/api/wayfinder/delete-wayfinder-location/${locationId}`,
        {
          data: { userId },
        },
      );
      await fetchData();
      showMsgSuccessToast("Location deleted successfully!");
    } catch (error) {
      console.error("Error deleting location:", error);
      showErrorToast("Failed to delete location");
    }
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();

    if (!newLocationName.trim()) {
      showErrorToast("Please enter a location name");
      return;
    }

    setIsAddingLocation(true);
    try {
      // Create the new location
      const locationData = {
        userId,
        name: newLocationName.trim(),
        buttonText: newLocationName.trim(),
        status: true,
        currentLanguage: currentLanguage, // Add currentLanguage parameter
      };

      // Add category/subcategory if selected
      if (newLocationSubcategory) {
        locationData.subCategoryId = newLocationSubcategory;
      } else if (newLocationCategory) {
        locationData.categoryId = newLocationCategory;
      }

      const response = await axios.post(
        "/api/wayfinder/locations",
        locationData,
      );

      if (response.data.success) {
        const newLocationId = response.data.data._id;

        // If category or subcategory is selected, add location to their navigation
        if (newLocationSubcategory) {
          const subcategory = subcategories.find(
            (s) => s._id === newLocationSubcategory,
          );
          const updatedLocations = [
            ...(subcategory.navigation?.locations || []),
            newLocationId,
          ];

          await axios.patch(
            `/api/wayfinder/subcategory/${newLocationSubcategory}`,
            {
              navigation: {
                ...subcategory.navigation,
                type: "location",
                status: true,
                locations: updatedLocations,
              },
            },
          );

          // Set order for the new location (assign it to the bottom)
          const existingLocations = getLocationsBySubcategory(newLocationSubcategory);
          
          // Find the maximum order number among existing locations
          let maxOrder = 0;
          existingLocations.forEach(loc => {
            const key = `subcategory_${newLocationSubcategory}_${loc._id}`;
            const order = locationOrders.get(key);
            if (order && order > maxOrder) {
              maxOrder = order;
            }
          });
          
          const newOrder = maxOrder + 1; // New location gets next order number

          await axios.post("/api/locationOrder/location-order", {
            userId,
            locationData: [{ locationId: newLocationId, order: newOrder }],
            parentType: 'subcategory',
            parentId: newLocationSubcategory,
          });
        } else if (newLocationCategory) {
          const category = categories.find(
            (c) => c._id === newLocationCategory,
          );
          const updatedLocations = [
            ...(category.navigation?.locations || []),
            newLocationId,
          ];

          await axios.patch(
            `/api/wayfinder/categories/${newLocationCategory}`,
            {
              navigation: {
                ...category.navigation,
                type: "location",
                status: true,
                locations: updatedLocations,
              },
            },
          );

          // Set order for the new location (assign it to the bottom)
          const existingLocations = getLocationsByCategory(newLocationCategory);
          
          // Find the maximum order number among existing locations
          let maxOrder = 0;
          existingLocations.forEach(loc => {
            const key = `category_${newLocationCategory}_${loc._id}`;
            const order = locationOrders.get(key);
            if (order && order > maxOrder) {
              maxOrder = order;
            }
          });
          
          const newOrder = maxOrder + 1; // New location gets next order number

          await axios.post("/api/locationOrder/location-order", {
            userId,
            locationData: [{ locationId: newLocationId, order: newOrder }],
            parentType: 'category',
            parentId: newLocationCategory,
          });
        }

        // Reset form and refresh data
        setNewLocationName("");
        setNewLocationCategory("");
        setNewLocationSubcategory("");
        setShowAddLocationForm(false);
        await fetchData();
        showMsgSuccessToast("Location added successfully!");
      }
    } catch (error) {
      console.error("Error adding location:", error);
      showErrorToast("Failed to add location");
    } finally {
      setIsAddingLocation(false);
    }
  };

  // Quick add location directly to a specific parent (category or subcategory)
  const handleQuickAddLocation = async (parentId, parentType) => {
    const formData = quickAddForms[parentId];
    if (!formData || !formData.name.trim()) {
      showErrorToast("Please enter a location name");
      return;
    }

    // Set loading state
    setQuickAddForms((prev) => ({
      ...prev,
      [parentId]: { ...prev[parentId], loading: true },
    }));

    try {
      // Create the new location
      const locationData = {
        userId,
        name: formData.name.trim(),
        buttonText: formData.name.trim(),
        status: true,
        currentLanguage: currentLanguage, // Add currentLanguage parameter
      };

      if (parentType === "subcategory") {
        locationData.subCategoryId = parentId;
      } else if (parentType === "category") {
        locationData.categoryId = parentId;
      }

      const response = await axios.post(
        "/api/wayfinder/locations",
        locationData,
      );

      if (response.data.success) {
        const newLocationId = response.data.data._id;

        // Add location to parent's navigation
        if (parentType === "subcategory") {
          const subcategory = subcategories.find((s) => s._id === parentId);
          const updatedLocations = [
            ...(subcategory.navigation?.locations || []),
            newLocationId,
          ];

          await axios.patch(`/api/wayfinder/subcategory/${parentId}`, {
            navigation: {
              ...subcategory.navigation,
              type: "location",
              status: true,
              locations: updatedLocations,
            },
          });

          // Set order for the new location (assign it to the bottom)
          const existingLocations = getLocationsBySubcategory(parentId);
          
          // Find the maximum order number among existing locations
          let maxOrder = 0;
          existingLocations.forEach(loc => {
            const key = `subcategory_${parentId}_${loc._id}`;
            const order = locationOrders.get(key);
            if (order && order > maxOrder) {
              maxOrder = order;
            }
          });
          
          const newOrder = maxOrder + 1; // New location gets next order number

          await axios.post("/api/locationOrder/location-order", {
            userId,
            locationData: [{ locationId: newLocationId, order: newOrder }],
            parentType: 'subcategory',
            parentId: parentId,
          });
        } else if (parentType === "category") {
          const category = categories.find((c) => c._id === parentId);
          const updatedLocations = [
            ...(category.navigation?.locations || []),
            newLocationId,
          ];

          await axios.patch(`/api/wayfinder/categories/${parentId}`, {
            navigation: {
              ...category.navigation,
              type: "location",
              status: true,
              locations: updatedLocations,
            },
          });

          // Set order for the new location (assign it to the bottom)
          const existingLocations = getLocationsByCategory(parentId);
          
          // Find the maximum order number among existing locations
          let maxOrder = 0;
          existingLocations.forEach(loc => {
            const key = `category_${parentId}_${loc._id}`;
            const order = locationOrders.get(key);
            if (order && order > maxOrder) {
              maxOrder = order;
            }
          });
          
          const newOrder = maxOrder + 1; // New location gets next order number

          await axios.post("/api/locationOrder/location-order", {
            userId,
            locationData: [{ locationId: newLocationId, order: newOrder }],
            parentType: 'category',
            parentId: parentId,
          });
        }

        // Reset form and refresh data
        setQuickAddForms((prev) => {
          const newForms = { ...prev };
          delete newForms[parentId];
          return newForms;
        });
        await fetchData();
        showMsgSuccessToast("Location added successfully!");
      }
    } catch (error) {
      console.error("Error adding location:", error);
      showErrorToast("Failed to add location");
    } finally {
      // Clear loading state
      setQuickAddForms((prev) => ({
        ...prev,
        [parentId]: { ...prev[parentId], loading: false },
      }));
    }
  };

  const toggleQuickAddForm = (parentId) => {
    setQuickAddForms((prev) => {
      if (prev[parentId]?.show) {
        // Close the form
        const newForms = { ...prev };
        delete newForms[parentId];
        return newForms;
      } else {
        // Open the form
        return {
          ...prev,
          [parentId]: { show: true, name: "", loading: false },
        };
      }
    });
  };

  const getAvailableSubcategories = () => {
    if (!newLocationCategory) return [];
    return subcategories.filter((sub) => {
      if (!sub.parentCategories || sub.parentCategories.length === 0)
        return false;
      return sub.parentCategories.some((parentId) => {
        const id = typeof parentId === "string" ? parentId : parentId._id;
        return id === newLocationCategory;
      });
    });
  };

  const getLocationsByCategory = (categoryId) => {
    const category = categories.find((c) => c._id === categoryId);
    if (!category?.navigation?.locations) return [];

    // Remove duplicate location IDs first
    const uniqueLocationIds = [...new Set(
      category.navigation.locations.map(locId => 
        typeof locId === "string" ? locId : locId._id
      )
    )];

    const categoryLocations = uniqueLocationIds
      .map((id) => {
        return locations.find((loc) => loc._id === id);
      })
      .filter(Boolean);

    // Sort locations: first by order from LocationOrder (ascending), then alphabetically for unordered
    return categoryLocations.sort((a, b) => {
      // Get order from the locationOrders Map using composite key: category_categoryId_locationId
      const keyA = `category_${categoryId}_${a._id}`;
      const keyB = `category_${categoryId}_${b._id}`;
      const orderA = locationOrders.get(keyA) ?? Infinity;
      const orderB = locationOrders.get(keyB) ?? Infinity;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // If both have same order (or both are unordered), sort alphabetically
      const nameA = getLanguageText(a.name, currentLanguage) || '';
      const nameB = getLanguageText(b.name, currentLanguage) || '';
      return nameA.localeCompare(nameB);
    });
  };

  const getLocationsBySubcategory = (subcategoryId) => {
    const subcategory = subcategories.find((s) => s._id === subcategoryId);
    if (!subcategory?.navigation?.locations) return [];

    // Remove duplicate location IDs first
    const uniqueLocationIds = [...new Set(
      subcategory.navigation.locations.map(locId => 
        typeof locId === "string" ? locId : locId._id
      )
    )];

    const subcategoryLocations = uniqueLocationIds
      .map((id) => {
        return locations.find((loc) => loc._id === id);
      })
      .filter(Boolean);

    // Sort locations: first by order from LocationOrder (ascending), then alphabetically for unordered
    return subcategoryLocations.sort((a, b) => {
      // Get order from the locationOrders Map using composite key: subcategory_subcategoryId_locationId
      const keyA = `subcategory_${subcategoryId}_${a._id}`;
      const keyB = `subcategory_${subcategoryId}_${b._id}`;
      const orderA = locationOrders.get(keyA) ?? Infinity;
      const orderB = locationOrders.get(keyB) ?? Infinity;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // If both have same order (or both are unordered), sort alphabetically
      const nameA = getLanguageText(a.name, currentLanguage) || '';
      const nameB = getLanguageText(b.name, currentLanguage) || '';
      return nameA.localeCompare(nameB);
    });
  };

  const getSubcategoriesByCategory = (categoryId) => {
    // Get subcategories that have this category as a parent
    return subcategories.filter((sub) => {
      if (!sub.parentCategories || sub.parentCategories.length === 0)
        return false;

      return sub.parentCategories.some((parentId) => {
        const id = typeof parentId === "string" ? parentId : parentId._id;
        return id === categoryId;
      });
    });
  };

  const getUnassignedLocations = () => {
    // Get all location IDs that are assigned to categories or subcategories
    const assignedLocationIds = new Set();

    // Add locations from categories
    categories.forEach((category) => {
      if (category.navigation?.locations) {
        category.navigation.locations.forEach((locId) => {
          const id = typeof locId === "string" ? locId : locId._id;
          assignedLocationIds.add(id);
        });
      }
    });

    // Add locations from subcategories
    subcategories.forEach((subcategory) => {
      if (subcategory.navigation?.locations) {
        subcategory.navigation.locations.forEach((locId) => {
          const id = typeof locId === "string" ? locId : locId._id;
          assignedLocationIds.add(id);
        });
      }
    });

    // Return locations that are not in the assigned set, sorted by order from LocationOrder then alphabetically
    const unassigned = locations.filter((loc) => !assignedLocationIds.has(loc._id));
    
    return unassigned.sort((a, b) => {
      // Get order from the locationOrders Map (fetched from LocationOrder collection)
      const orderA = locationOrders.get(a._id) ?? Infinity;
      const orderB = locationOrders.get(b._id) ?? Infinity;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // If both have same order (or both are unordered), sort alphabetically
      const nameA = getLanguageText(a.name, currentLanguage) || '';
      const nameB = getLanguageText(b.name, currentLanguage) || '';
      return nameA.localeCompare(nameB);
    });
  };

  const moveLocation = async (locationId, direction, parentType, parentId) => {
    try {
      // Get all locations for this parent
      let locationsList;
      if (parentType === "category") {
        locationsList = getLocationsByCategory(parentId);
      } else {
        locationsList = getLocationsBySubcategory(parentId);
      }

      const currentIndex = locationsList.findIndex(
        (loc) => loc._id === locationId,
      );
      if (currentIndex === -1) return;

      const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= locationsList.length) return;

      // Swap positions in the array
      [locationsList[currentIndex], locationsList[newIndex]] = [
        locationsList[newIndex],
        locationsList[currentIndex],
      ];

      // Save the new order to LocationOrder schema with parent context
      const locationData = locationsList.map((loc, index) => ({
        locationId: loc._id,
        order: index + 1, // 1-based ordering
      }));

      await axios.post("/api/locationOrder/location-order", {
        userId,
        locationData,
        parentType, // 'category' or 'subcategory'
        parentId,   // The category or subcategory ID
      });

      // Also update the navigation array for backward compatibility
      const parent =
        parentType === "category"
          ? categories.find((c) => c._id === parentId)
          : subcategories.find((s) => s._id === parentId);

      const endpoint =
        parentType === "category"
          ? `/api/wayfinder/categories/${parentId}`
          : `/api/wayfinder/subcategory/${parentId}`;

      await axios.patch(endpoint, {
        navigation: {
          ...parent.navigation,
          locations: locationsList.map((loc) => loc._id),
        },
      });

      // Refresh data
      await fetchData();
      showMsgSuccessToast("Location order updated successfully!");
    } catch (error) {
      console.error("Error moving location:", error);
      showErrorToast("Failed to update location order");
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e, locationId, parentType, parentId) => {
    setDraggedItem({ locationId, parentType, parentId });
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragOver = (e, locationId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverItem(locationId);
  };

  const handleDragLeave = (e) => {
    setDragOverItem(null);
  };

  const handleDrop = async (e, targetLocationId, parentType, parentId) => {
    e.preventDefault();

    if (!draggedItem || draggedItem.locationId === targetLocationId) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Only allow drops within the same parent
    if (
      draggedItem.parentType !== parentType ||
      draggedItem.parentId !== parentId
    ) {
      showErrorToast(
        "Cannot move locations between different categories/subcategories",
      );
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    try {
      // Get all locations for this parent
      let locationsList;
      if (parentType === "category") {
        locationsList = getLocationsByCategory(parentId);
      } else {
        locationsList = getLocationsBySubcategory(parentId);
      }

      const draggedIndex = locationsList.findIndex(
        (loc) => loc._id === draggedItem.locationId,
      );
      const targetIndex = locationsList.findIndex(
        (loc) => loc._id === targetLocationId,
      );

      if (draggedIndex === -1 || targetIndex === -1) return;

      // Remove dragged item and insert at target position
      const [removed] = locationsList.splice(draggedIndex, 1);
      locationsList.splice(targetIndex, 0, removed);

      // Save the new order to LocationOrder schema with parent context
      const locationData = locationsList.map((loc, index) => ({
        locationId: loc._id,
        order: index + 1, // 1-based ordering
      }));

      await axios.post("/api/locationOrder/location-order", {
        userId,
        locationData,
        parentType, // 'category' or 'subcategory'
        parentId,   // The category or subcategory ID
      });

      // Also update the navigation array for backward compatibility
      const parent =
        parentType === "category"
          ? categories.find((c) => c._id === parentId)
          : subcategories.find((s) => s._id === parentId);

      const endpoint =
        parentType === "category"
          ? `/api/wayfinder/categories/${parentId}`
          : `/api/wayfinder/subcategory/${parentId}`;

      await axios.patch(endpoint, {
        navigation: {
          ...parent.navigation,
          locations: locationsList.map((loc) => loc._id),
        },
      });

      // Refresh data
      await fetchData();
      showMsgSuccessToast("Location order updated successfully!");
    } catch (error) {
      console.error("Error reordering location:", error);
      showErrorToast("Failed to update location order");
    } finally {
      setDraggedItem(null);
      setDragOverItem(null);
    }
  };

  // Enhanced filtering logic that includes location search
  const searchLower = searchTerm.toLowerCase();

  // Helper function to check if a category has matching locations
  const categoryHasMatchingLocations = (categoryId) => {
    const categoryLocations = getLocationsByCategory(categoryId);
    return categoryLocations.some((loc) =>
      getLanguageText(loc.name, currentLanguage)?.toLowerCase().includes(searchLower),
    );
  };

  // Helper function to check if a subcategory has matching locations
  const subcategoryHasMatchingLocations = (subcategoryId) => {
    const subLocations = getLocationsBySubcategory(subcategoryId);
    return subLocations.some((loc) =>
      getLanguageText(loc.name, currentLanguage)?.toLowerCase().includes(searchLower),
    );
  };

  // Filter categories: show if name matches OR has matching locations OR has subcategories with matching locations
  const filteredCategories = categories.filter((cat) => {
    const nameMatches = getLanguageText(cat.name, currentLanguage)?.toLowerCase().includes(searchLower);
    const hasMatchingLocations = categoryHasMatchingLocations(cat._id);

    // Check if any subcategories under this category have matching locations
    const categorySubcategories = getSubcategoriesByCategory(cat._id);
    const hasSubcategoriesWithMatchingLocations = categorySubcategories.some(
      (sub) => subcategoryHasMatchingLocations(sub._id),
    );

    return (
      nameMatches ||
      hasMatchingLocations ||
      hasSubcategoriesWithMatchingLocations
    );
  });

  // Filter subcategories: show if name matches OR has matching locations
  const filteredSubcategories = subcategories.filter((sub) => {
    const nameMatches = getLanguageText(sub.name, currentLanguage)?.toLowerCase().includes(searchLower);
    const hasMatchingLocations = subcategoryHasMatchingLocations(sub._id);
    return nameMatches || hasMatchingLocations;
  });

  // Filter locations within categories and subcategories
  const getFilteredLocationsByCategory = (categoryId) => {
    const categoryLocations = getLocationsByCategory(categoryId);
    if (!searchTerm) return categoryLocations;
    return categoryLocations.filter((loc) =>
      getLanguageText(loc.name, currentLanguage)?.toLowerCase().includes(searchLower),
    );
  };

  const getFilteredLocationsBySubcategory = (subcategoryId) => {
    const subLocations = getLocationsBySubcategory(subcategoryId);
    if (!searchTerm) return subLocations;
    return subLocations.filter((loc) =>
      getLanguageText(loc.name, currentLanguage)?.toLowerCase().includes(searchLower),
    );
  };

  // Auto-expand categories/subcategories that have matching locations when searching
  useEffect(() => {
    if (searchTerm) {
      const newExpandedCategories = {};
      const newExpandedSubcategories = {};

      // Expand categories with matching locations
      filteredCategories.forEach((cat) => {
        if (categoryHasMatchingLocations(cat._id)) {
          newExpandedCategories[cat._id] = true;
        }

        // Also expand if subcategories have matching locations
        const categorySubcategories = getSubcategoriesByCategory(cat._id);
        categorySubcategories.forEach((sub) => {
          if (subcategoryHasMatchingLocations(sub._id)) {
            newExpandedCategories[cat._id] = true;
            newExpandedSubcategories[sub._id] = true;
          }
        });
      });

      // Expand standalone subcategories with matching locations
      filteredSubcategories.forEach((sub) => {
        if (subcategoryHasMatchingLocations(sub._id)) {
          newExpandedSubcategories[sub._id] = true;
        }
      });

      setExpandedCategories(newExpandedCategories);
      setExpandedSubcategories(newExpandedSubcategories);
    }
  }, [searchTerm, categories, subcategories, locations]);

  const unassignedLocations = getUnassignedLocations();
  const filteredUnassignedLocations = unassignedLocations.filter((loc) =>
    getLanguageText(loc.name, currentLanguage)?.toLowerCase().includes(searchLower),
  );

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
        Loading locations...
      </div>
    );
  }

  return (
    <div className="location-manager-card">
      {/* Add Location Button */}
      <div
        style={{
          marginBottom: "15px",
          display: "flex",
          gap: "10px",
          alignItems: "center",
        }}
      >
        <button
          className="btn btn-primary"
          onClick={() => setShowAddLocationForm(!showAddLocationForm)}
          style={{
            backgroundColor: buttonColor || "#6366f1",
            border: "none",
            padding: "8px 16px",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          <span className="bi bi-plus-circle"></span>
          {showAddLocationForm ? "Cancel" : "Add New Location"}
        </button>
      </div>

      {/* Add Location Form */}
      {showAddLocationForm && (
        <div
          style={{
            backgroundColor: "#f8f9fa",
            padding: "15px",
            borderRadius: "8px",
            marginBottom: "15px",
            border: "1px solid #e2e8f0",
          }}
        >
          <form onSubmit={handleAddLocation}>
            <div className="form-group" style={{ marginBottom: "12px" }}>
              <label
                style={{
                  fontSize: "13px",
                  fontWeight: "500",
                  marginBottom: "6px",
                  display: "block",
                }}
              >
                Location Name *
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter location name"
                value={newLocationName}
                onChange={(e) => setNewLocationName(e.target.value)}
                required
                style={{ padding: "8px 12px", fontSize: "14px" }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: "12px" }}>
              <label
                style={{
                  fontSize: "13px",
                  fontWeight: "500",
                  marginBottom: "6px",
                  display: "block",
                }}
              >
                Category (Optional)
              </label>
              <select
                className="form-control"
                value={newLocationCategory}
                onChange={(e) => {
                  setNewLocationCategory(e.target.value);
                  setNewLocationSubcategory(""); // Reset subcategory when category changes
                }}
                style={{ padding: "8px 12px", fontSize: "14px" }}
              >
                <option value="">-- No Category --</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {getLanguageText(cat.name, currentLanguage) || getLanguageText(cat.buttonText, currentLanguage)}
                  </option>
                ))}
              </select>
            </div>

            {newLocationCategory && getAvailableSubcategories().length > 0 && (
              <div className="form-group" style={{ marginBottom: "12px" }}>
                <label
                  style={{
                    fontSize: "13px",
                    fontWeight: "500",
                    marginBottom: "6px",
                    display: "block",
                  }}
                >
                  Subcategory (Optional)
                </label>
                <select
                  className="form-control"
                  value={newLocationSubcategory}
                  onChange={(e) => setNewLocationSubcategory(e.target.value)}
                  style={{ padding: "8px 12px", fontSize: "14px" }}
                >
                  <option value="">-- No Subcategory --</option>
                  {getAvailableSubcategories().map((sub) => (
                    <option key={sub._id} value={sub._id}>
                      {getLanguageText(sub.name, currentLanguage) || getLanguageText(sub.buttonText, currentLanguage)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddLocationForm(false);
                  setNewLocationName("");
                  setNewLocationCategory("");
                  setNewLocationSubcategory("");
                }}
                style={{
                  padding: "6px 14px",
                  fontSize: "13px",
                  borderRadius: "6px",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isAddingLocation}
                style={{
                  backgroundColor: buttonColor || "#6366f1",
                  border: "none",
                  padding: "6px 14px",
                  fontSize: "13px",
                  borderRadius: "6px",
                }}
              >
                {isAddingLocation ? "Adding..." : "Add Location"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search Input */}
      <div className="form-group" style={{ marginBottom: "15px" }}>
        <div style={{ position: "relative" }}>
          <span
            className="bi bi-search"
            style={{
              position: "absolute",
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#999",
              pointerEvents: "none",
            }}
          ></span>
          <input
            type="text"
            className="form-control"
            placeholder="Search categories, subcategories, or locations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "10px 10px 10px 35px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                color: "#999",
                cursor: "pointer",
                fontSize: "18px",
                padding: "0 5px",
              }}
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>
        {searchTerm && (
          <div
            style={{
              fontSize: "12px",
              color: "#666",
              marginTop: "5px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <span className="bi bi-info-circle"></span>
            Showing results for "{searchTerm}"
          </div>
        )}
      </div>

      {/* Location Manager List */}
      <ul className="submenu-list" style={{ minHeight: "200px" }}>
        {filteredCategories.length === 0 &&
          filteredSubcategories.length === 0 &&
          filteredUnassignedLocations.length === 0 ? (
          <li style={{ textAlign: "center", padding: "20px", color: "#999" }}>
            {searchTerm
              ? "No categories, subcategories, or locations found"
              : "No categories available"}
          </li>
        ) : (
          <>
            {/* Categories */}
            {filteredCategories.map((category) => {
              const categoryLocations = getFilteredLocationsByCategory(
                category._id,
              );
              const categorySubcategories = getSubcategoriesByCategory(
                category._id,
              );
              const isExpanded = expandedCategories[category._id];
              const hasContent =
                (category.navigation?.type === "location" &&
                  categoryLocations.length > 0) ||
                (category.navigation?.type === "subcategory" &&
                  categorySubcategories.length > 0);

              return (
                <li key={category._id} className="category-group">
                  {/* Category Header */}
                  <div className="submenu-item category-item">
                    <span
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                      onClick={() => toggleCategory(category._id)}
                    >
                      <span className="toggle-arrow">
                        {isExpanded ? "▼" : "▶"}
                      </span>
                      <strong>
                        📁 {getLanguageText(category.name, currentLanguage) || getLanguageText(category.buttonText, currentLanguage) || "Untitled"}
                      </strong>
                      <span style={{ fontSize: "12px", color: "#666" }}>
                        ({categoryLocations.length} locations
                        {categorySubcategories.length > 0 &&
                          `, ${categorySubcategories.length} subcategories`}
                        )
                      </span>
                    </span>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      {/* Quick Add Location Button */}
                      <button
                        className="btn btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleQuickAddForm(category._id);
                        }}
                        title="Add location to this category"
                        style={{
                          backgroundColor: buttonColor || "#6366f1",
                          color: "white",
                          border: "none",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <span className="bi bi-plus-circle"></span>
                        Add Location
                      </button>

                      {/* Settings Icon */}
                      <Link
                        to={`/wayfinder-category/${userId}/${category._id}`}
                      >
                        <span
                          className="bi bi-gear micon"
                          title="Category Settings"
                        ></span>
                      </Link>
                    </div>
                  </div>

                  {/* Quick Add Location Form for Category */}
                  {quickAddForms[category._id]?.show && (
                    <div
                      style={{
                        backgroundColor: "#f0f9ff",
                        padding: "10px 15px",
                        margin: "5px 0",
                        borderRadius: "6px",
                        border: "1px solid #bfdbfe",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter location name..."
                          value={quickAddForms[category._id]?.name || ""}
                          onChange={(e) => {
                            setQuickAddForms((prev) => ({
                              ...prev,
                              [category._id]: {
                                ...prev[category._id],
                                name: e.target.value,
                              },
                            }));
                          }}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              handleQuickAddLocation(category._id, "category");
                            }
                          }}
                          style={{
                            flex: 1,
                            padding: "6px 10px",
                            fontSize: "13px",
                          }}
                          autoFocus
                        />
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() =>
                            handleQuickAddLocation(category._id, "category")
                          }
                          disabled={quickAddForms[category._id]?.loading}
                          style={{
                            backgroundColor: buttonColor || "#6366f1",
                            border: "none",
                            padding: "6px 12px",
                            fontSize: "13px",
                          }}
                        >
                          {quickAddForms[category._id]?.loading
                            ? "Adding..."
                            : "Add"}
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => toggleQuickAddForm(category._id)}
                          style={{
                            padding: "6px 12px",
                            fontSize: "13px",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Category Content */}
                  {isExpanded &&
                    (categoryLocations.length > 0 ||
                      categorySubcategories.length > 0) && (
                      <ul className="nested-list">
                        {/* Direct Locations under Category */}
                        {categoryLocations.length > 0 &&
                          categoryLocations.map((location, index) => (
                            <li
                              key={location._id}
                              className={`submenu-item location-item ${dragOverItem === location._id ? "drag-over" : ""}`}
                              draggable
                              onDragStart={(e) =>
                                handleDragStart(
                                  e,
                                  location._id,
                                  "category",
                                  category._id,
                                )
                              }
                              onDragEnd={handleDragEnd}
                              onDragOver={(e) =>
                                handleDragOver(e, location._id)
                              }
                              onDragLeave={handleDragLeave}
                              onDrop={(e) =>
                                handleDrop(
                                  e,
                                  location._id,
                                  "category",
                                  category._id,
                                )
                              }
                            >
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <span
                                  className="drag-handle"
                                  title="Drag to reorder"
                                >
                                  ⋮⋮
                                </span>
                                <span style={{ marginLeft: "10px" }}>
                                  📍 {getLanguageText(location.name, currentLanguage)}
                                </span>
                              </span>

                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                {/* Order Buttons */}
                                <button
                                  className="order-btn"
                                  onClick={() =>
                                    moveLocation(
                                      location._id,
                                      "up",
                                      "category",
                                      category._id,
                                    )
                                  }
                                  disabled={index === 0}
                                  title="Move up"
                                >
                                  ↑
                                </button>
                                <button
                                  className="order-btn"
                                  onClick={() =>
                                    moveLocation(
                                      location._id,
                                      "down",
                                      "category",
                                      category._id,
                                    )
                                  }
                                  disabled={
                                    index === categoryLocations.length - 1
                                  }
                                  title="Move down"
                                >
                                  ↓
                                </button>

                                {/* Visibility Toggle */}
                                {location.status ? (
                                  <span
                                    className="bi bi-eye micon"
                                    onClick={() =>
                                      toggleLocationVisibility(
                                        location._id,
                                        location.status,
                                      )
                                    }
                                    title="Hide location"
                                  ></span>
                                ) : (
                                  <span
                                    className="bi bi-eye-slash micon"
                                    onClick={() =>
                                      toggleLocationVisibility(
                                        location._id,
                                        location.status,
                                      )
                                    }
                                    title="Show location"
                                  ></span>
                                )}

                                {/* Settings Icon */}
                                <Link
                                  to={`/wayfinder-locations/${userId}/${location._id}`}
                                >
                                  <span
                                    className="bi bi-gear micon"
                                    title="Location Settings"
                                  ></span>
                                </Link>

                                {/* Delete Icon */}
                                <span
                                  className="bi bi-trash micon"
                                  onClick={() => deleteLocation(location._id)}
                                  title="Delete location"
                                ></span>
                              </div>
                            </li>
                          ))}

                        {/* Subcategories under Category - Always show if they exist */}
                        {categorySubcategories.length > 0 &&
                          categorySubcategories.map((subcategory) => {
                            const subLocations =
                              getFilteredLocationsBySubcategory(
                                subcategory._id,
                              );
                            const isSubExpanded =
                              expandedSubcategories[subcategory._id];

                            return (
                              <li
                                key={subcategory._id}
                                className="subcategory-group"
                              >
                                {/* Subcategory Header */}
                                <div className="submenu-item subcategory-item">
                                  <span
                                    style={{
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                    }}
                                    onClick={() =>
                                      toggleSubcategory(subcategory._id)
                                    }
                                  >
                                    <span
                                      className="toggle-arrow"
                                      style={{ marginLeft: "20px" }}
                                    >
                                      {isSubExpanded ? "▼" : "▶"}
                                    </span>
                                    <strong>
                                      📂{" "}
                                      {getLanguageText(subcategory.name, currentLanguage) ||
                                        getLanguageText(subcategory.buttonText, currentLanguage) ||
                                        "Untitled"}
                                    </strong>
                                    <span
                                      style={{
                                        fontSize: "12px",
                                        color: "#666",
                                      }}
                                    >
                                      ({subLocations.length} locations)
                                    </span>
                                  </span>

                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "5px",
                                    }}
                                  >
                                    {/* Quick Add Location Button */}
                                    <button
                                      className="btn btn-sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleQuickAddForm(subcategory._id);
                                      }}
                                      title="Add location to this subcategory"
                                      style={{
                                        backgroundColor:
                                          buttonColor || "#6366f1",
                                        color: "white",
                                        border: "none",
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        fontSize: "12px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <span className="bi bi-plus-circle"></span>
                                      Add Location
                                    </button>

                                    {/* Settings Icon */}
                                    <Link
                                      to={`/wayfinder-sub-category/${userId}/${subcategory._id}`}
                                    >
                                      <span
                                        className="bi bi-gear micon"
                                        title="Subcategory Settings"
                                      ></span>
                                    </Link>
                                  </div>
                                </div>

                                {/* Quick Add Location Form for Subcategory */}
                                {quickAddForms[subcategory._id]?.show && (
                                  <div
                                    style={{
                                      backgroundColor: "#f0f9ff",
                                      padding: "10px 15px",
                                      margin: "5px 0 5px 40px",
                                      borderRadius: "6px",
                                      border: "1px solid #bfdbfe",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "8px",
                                        alignItems: "center",
                                      }}
                                    >
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter location name..."
                                        value={
                                          quickAddForms[subcategory._id]
                                            ?.name || ""
                                        }
                                        onChange={(e) => {
                                          setQuickAddForms((prev) => ({
                                            ...prev,
                                            [subcategory._id]: {
                                              ...prev[subcategory._id],
                                              name: e.target.value,
                                            },
                                          }));
                                        }}
                                        onKeyPress={(e) => {
                                          if (e.key === "Enter") {
                                            handleQuickAddLocation(
                                              subcategory._id,
                                              "subcategory",
                                            );
                                          }
                                        }}
                                        style={{
                                          flex: 1,
                                          padding: "6px 10px",
                                          fontSize: "13px",
                                        }}
                                        autoFocus
                                      />
                                      <button
                                        className="btn btn-sm btn-primary"
                                        onClick={() =>
                                          handleQuickAddLocation(
                                            subcategory._id,
                                            "subcategory",
                                          )
                                        }
                                        disabled={
                                          quickAddForms[subcategory._id]
                                            ?.loading
                                        }
                                        style={{
                                          backgroundColor:
                                            buttonColor || "#6366f1",
                                          border: "none",
                                          padding: "6px 12px",
                                          fontSize: "13px",
                                        }}
                                      >
                                        {quickAddForms[subcategory._id]?.loading
                                          ? "Adding..."
                                          : "Add"}
                                      </button>
                                      <button
                                        className="btn btn-sm btn-secondary"
                                        onClick={() =>
                                          toggleQuickAddForm(subcategory._id)
                                        }
                                        style={{
                                          padding: "6px 12px",
                                          fontSize: "13px",
                                        }}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {/* Subcategory Locations */}
                                {isSubExpanded && subLocations.length > 0 && (
                                  <ul className="nested-list">
                                    {subLocations.map((location, index) => (
                                      <li
                                        key={location._id}
                                        className={`submenu-item location-item ${dragOverItem === location._id ? "drag-over" : ""}`}
                                        draggable
                                        onDragStart={(e) =>
                                          handleDragStart(
                                            e,
                                            location._id,
                                            "subcategory",
                                            subcategory._id,
                                          )
                                        }
                                        onDragEnd={handleDragEnd}
                                        onDragOver={(e) =>
                                          handleDragOver(e, location._id)
                                        }
                                        onDragLeave={handleDragLeave}
                                        onDrop={(e) =>
                                          handleDrop(
                                            e,
                                            location._id,
                                            "subcategory",
                                            subcategory._id,
                                          )
                                        }
                                      >
                                        <span
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                          }}
                                        >
                                          <span
                                            className="drag-handle"
                                            title="Drag to reorder"
                                          >
                                            ⋮⋮
                                          </span>
                                          <span style={{ marginLeft: "30px" }}>
                                            📍 {getLanguageText(location.name, currentLanguage)}
                                          </span>
                                        </span>

                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "5px",
                                          }}
                                        >
                                          {/* Order Buttons */}
                                          <button
                                            className="order-btn"
                                            onClick={() =>
                                              moveLocation(
                                                location._id,
                                                "up",
                                                "subcategory",
                                                subcategory._id,
                                              )
                                            }
                                            disabled={index === 0}
                                            title="Move up"
                                          >
                                            ↑
                                          </button>
                                          <button
                                            className="order-btn"
                                            onClick={() =>
                                              moveLocation(
                                                location._id,
                                                "down",
                                                "subcategory",
                                                subcategory._id,
                                              )
                                            }
                                            disabled={
                                              index === subLocations.length - 1
                                            }
                                            title="Move down"
                                          >
                                            ↓
                                          </button>

                                          {/* Visibility Toggle */}
                                          {location.status ? (
                                            <span
                                              className="bi bi-eye micon"
                                              onClick={() =>
                                                toggleLocationVisibility(
                                                  location._id,
                                                  location.status,
                                                )
                                              }
                                              title="Hide location"
                                            ></span>
                                          ) : (
                                            <span
                                              className="bi bi-eye-slash micon"
                                              onClick={() =>
                                                toggleLocationVisibility(
                                                  location._id,
                                                  location.status,
                                                )
                                              }
                                              title="Show location"
                                            ></span>
                                          )}

                                          {/* Settings Icon */}
                                          <Link
                                            to={`/wayfinder-locations/${userId}/${location._id}`}
                                          >
                                            <span
                                              className="bi bi-gear micon"
                                              title="Location Settings"
                                            ></span>
                                          </Link>

                                          {/* Delete Icon */}
                                          <span
                                            className="bi bi-trash micon"
                                            onClick={() =>
                                              deleteLocation(location._id)
                                            }
                                            title="Delete location"
                                          ></span>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </li>
                            );
                          })}
                      </ul>
                    )}
                </li>
              );
            })}

            {/* Standalone Subcategories (not linked to any category) */}
            {filteredSubcategories
              .filter((sub) => {
                // Check if this subcategory has any parent categories
                if (
                  !sub.parentCategories ||
                  sub.parentCategories.length === 0
                ) {
                  return true; // Show if no parents at all
                }

                // Check if any of its parent categories are in the filtered categories list
                const hasVisibleParent = sub.parentCategories.some(
                  (parentId) => {
                    const id =
                      typeof parentId === "string" ? parentId : parentId._id;
                    return filteredCategories.some((cat) => cat._id === id);
                  },
                );

                // Only show as standalone if it has NO visible parent in the current filtered list
                return !hasVisibleParent;
              })
              .map((subcategory) => {
                const subLocations = getFilteredLocationsBySubcategory(
                  subcategory._id,
                );
                const isSubExpanded = expandedSubcategories[subcategory._id];

                return (
                  <li
                    key={subcategory._id}
                    className="subcategory-group standalone"
                  >
                    {/* Subcategory Header */}
                    <div className="submenu-item subcategory-item">
                      <span
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                        onClick={() => toggleSubcategory(subcategory._id)}
                      >
                        <span className="toggle-arrow">
                          {isSubExpanded ? "▼" : "▶"}
                        </span>
                        <strong>
                          📂{" "}
                          {getLanguageText(subcategory.name, currentLanguage) ||
                            getLanguageText(subcategory.buttonText, currentLanguage) ||
                            "Untitled"}
                        </strong>
                        <span style={{ fontSize: "12px", color: "#666" }}>
                          ({subLocations.length} locations)
                        </span>
                      </span>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        {/* Quick Add Location Button */}
                        <button
                          className="btn btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleQuickAddForm(subcategory._id);
                          }}
                          title="Add location to this subcategory"
                          style={{
                            backgroundColor: buttonColor || "#6366f1",
                            color: "white",
                            border: "none",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span className="bi bi-plus-circle"></span>
                          Add Location
                        </button>

                        {/* Settings Icon */}
                        <Link
                          to={`/wayfinder-sub-category/${userId}/${subcategory._id}`}
                        >
                          <span
                            className="bi bi-gear micon"
                            title="Subcategory Settings"
                          ></span>
                        </Link>
                      </div>
                    </div>

                    {/* Quick Add Location Form for Standalone Subcategory */}
                    {quickAddForms[subcategory._id]?.show && (
                      <div
                        style={{
                          backgroundColor: "#f0f9ff",
                          padding: "10px 15px",
                          margin: "5px 0",
                          borderRadius: "6px",
                          border: "1px solid #bfdbfe",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "center",
                          }}
                        >
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter location name..."
                            value={quickAddForms[subcategory._id]?.name || ""}
                            onChange={(e) => {
                              setQuickAddForms((prev) => ({
                                ...prev,
                                [subcategory._id]: {
                                  ...prev[subcategory._id],
                                  name: e.target.value,
                                },
                              }));
                            }}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                handleQuickAddLocation(
                                  subcategory._id,
                                  "subcategory",
                                );
                              }
                            }}
                            style={{
                              flex: 1,
                              padding: "6px 10px",
                              fontSize: "13px",
                            }}
                            autoFocus
                          />
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() =>
                              handleQuickAddLocation(
                                subcategory._id,
                                "subcategory",
                              )
                            }
                            disabled={quickAddForms[subcategory._id]?.loading}
                            style={{
                              backgroundColor: buttonColor || "#6366f1",
                              border: "none",
                              padding: "6px 12px",
                              fontSize: "13px",
                            }}
                          >
                            {quickAddForms[subcategory._id]?.loading
                              ? "Adding..."
                              : "Add"}
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => toggleQuickAddForm(subcategory._id)}
                            style={{
                              padding: "6px 12px",
                              fontSize: "13px",
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Subcategory Locations */}
                    {isSubExpanded && subLocations.length > 0 && (
                      <ul className="nested-list">
                        {subLocations.map((location, index) => (
                          <li
                            key={location._id}
                            className={`submenu-item location-item ${dragOverItem === location._id ? "drag-over" : ""}`}
                            draggable
                            onDragStart={(e) =>
                              handleDragStart(
                                e,
                                location._id,
                                "subcategory",
                                subcategory._id,
                              )
                            }
                            onDragEnd={handleDragEnd}
                            onDragOver={(e) => handleDragOver(e, location._id)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) =>
                              handleDrop(
                                e,
                                location._id,
                                "subcategory",
                                subcategory._id,
                              )
                            }
                          >
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <span
                                className="drag-handle"
                                title="Drag to reorder"
                              >
                                ⋮⋮
                              </span>
                              <span style={{ marginLeft: "10px" }}>
                                📍 {getLanguageText(location.name, currentLanguage)}
                              </span>
                            </span>

                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                              }}
                            >
                              {/* Order Buttons */}
                              <button
                                className="order-btn"
                                onClick={() =>
                                  moveLocation(
                                    location._id,
                                    "up",
                                    "subcategory",
                                    subcategory._id,
                                  )
                                }
                                disabled={index === 0}
                                title="Move up"
                              >
                                ↑
                              </button>
                              <button
                                className="order-btn"
                                onClick={() =>
                                  moveLocation(
                                    location._id,
                                    "down",
                                    "subcategory",
                                    subcategory._id,
                                  )
                                }
                                disabled={index === subLocations.length - 1}
                                title="Move down"
                              >
                                ↓
                              </button>

                              {/* Visibility Toggle */}
                              {location.status ? (
                                <span
                                  className="bi bi-eye micon"
                                  onClick={() =>
                                    toggleLocationVisibility(
                                      location._id,
                                      location.status,
                                    )
                                  }
                                  title="Hide location"
                                ></span>
                              ) : (
                                <span
                                  className="bi bi-eye-slash micon"
                                  onClick={() =>
                                    toggleLocationVisibility(
                                      location._id,
                                      location.status,
                                    )
                                  }
                                  title="Show location"
                                ></span>
                              )}

                              {/* Settings Icon */}
                              <Link
                                to={`/wayfinder-locations/${userId}/${location._id}`}
                              >
                                <span
                                  className="bi bi-gear micon"
                                  title="Location Settings"
                                ></span>
                              </Link>

                              {/* Delete Icon */}
                              <span
                                className="bi bi-trash micon"
                                onClick={() => deleteLocation(location._id)}
                                title="Delete location"
                              ></span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
          </>
        )}

        {/* Unassigned Locations Section */}
        {filteredUnassignedLocations.length > 0 && (
          <li
            className="unassigned-locations-group"
            style={{ marginTop: "20px" }}
          >
            {/* Unassigned Header */}
            <div
              className="submenu-item unassigned-header"
              style={{
                background: "linear-gradient(to right, #fef3c7, #ffffff)",
                borderLeft: "4px solid #f59e0b",
                padding: "12px 15px",
                fontWeight: 600,
              }}
            >
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <strong>📦 Unassigned Locations</strong>
                <span style={{ fontSize: "12px", color: "#666" }}>
                  ({filteredUnassignedLocations.length} locations)
                </span>
              </span>
            </div>

            {/* Unassigned Locations List */}
            <ul className="nested-list">
              {filteredUnassignedLocations.map((location) => (
                <li
                  key={location._id}
                  className="submenu-item location-item unassigned-location"
                  style={{
                    borderLeft: "3px solid #f59e0b",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span style={{ marginLeft: "10px" }}>
                      📍 {getLanguageText(location.name, currentLanguage)}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#f59e0b",
                        backgroundColor: "#fef3c7",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        fontWeight: "500",
                      }}
                    >
                      Not assigned
                    </span>
                  </span>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    {/* Visibility Toggle */}
                    {location.status ? (
                      <span
                        className="bi bi-eye micon"
                        onClick={() =>
                          toggleLocationVisibility(
                            location._id,
                            location.status,
                          )
                        }
                        title="Hide location"
                      ></span>
                    ) : (
                      <span
                        className="bi bi-eye-slash micon"
                        onClick={() =>
                          toggleLocationVisibility(
                            location._id,
                            location.status,
                          )
                        }
                        title="Show location"
                      ></span>
                    )}

                    {/* Settings Icon */}
                    <Link to={`/wayfinder-locations/${userId}/${location._id}`}>
                      <span
                        className="bi bi-gear micon"
                        title="Location Settings"
                      ></span>
                    </Link>

                    {/* Delete Icon */}
                    <span
                      className="bi bi-trash micon"
                      onClick={() => deleteLocation(location._id)}
                      title="Delete location"
                    ></span>
                  </div>
                </li>
              ))}
            </ul>
          </li>
        )}
      </ul>
    </div>
  );
};

export default LocationManagerCard;
